import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectPackageDetails } from 'store/shipping/shippingSelectors'
import { CollectionCard } from 'reusableComponents/Cards'
import styled from 'styled-components'
import { breakpoints, colors } from 'globalStyles'

import PackageDetailsSVG from 'assets/svg/package_details_ellipse.svg'
import ShowMoreModal from 'reusableComponents/ShowMoreModal'
import Button from 'react-bootstrap/Button'
import SVGIcon from 'reusableComponents/SVGIcon'
// import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
// import { faTruckFast, faCircleCheck, faPersonCarryBox } from '@fortawesome/pro-regular-svg-icons'
import { ReactComponent as TriangleExclamation } from 'assets/svg/triangle-exclamation.svg'
import { ReactComponent as TruckFast } from 'assets/svg/truck-fast.svg'
import { ReactComponent as CircleCheck } from 'assets/svg/regular-circle-check.svg'
import { ReactComponent as PersonCarryBox } from 'assets/svg/person-carry-box.svg'
import PackageDetailsCard from 'components/PackageDetailsCard'
import ToolTip from 'reusableComponents/ToolTip'
import PopUp from 'reusableComponents/PopUp'

const StyledDisplayContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  margin-right: 20px;
  margin-left: 18px;
  @media ${breakpoints.phone}, ${breakpoints.tablet} {
    margin-left: 11px;
    margin-right: 16px;
  }
  .addressStyle {
    font-size: 14px;
    line-height: 28px;
    display: inline-flex;
    max-width: 150px;
    word-break: break-all;
    @media ${breakpoints.desktop} {
      font-size: 16px;
    }
  }
  .addressIconStyle {
    text-align: center;
    display: inline-flex;
    flex-grow: 100;
    margin: 0 5px;
  }
  .addressLine {
    margin: 18px 2px 0px 2px;
    vertical-align: bottom;
    width: 45%;
    display: inline-block;
    border-top: 2px solid ${colors.primary_blue};
    line-height: 0;
  }
`

const StyledLinkButton = styled(Button)`
  font-size: 16px !important;
  line-height: 20px !important;
  margin: 0 auto 29px 31px;
  @media ${breakpoints.phone}, ${breakpoints.tablet} {
    font-size: 14px !important;
    margin-left: 24px;
  }
`

const StyledWrapper = styled.div`
  padding: 16px 0px 20px !important;
  @media ${breakpoints.desktop}, ${breakpoints.tablet} {
    padding: 24px 0 !important;
  }
`

const statusRespSize = {
  phone: { size: '20px' },
  tablet: { size: '20px' },
  desktop: { size: '19px' },
}

const statusIconMapping = {
  1: <SVGIcon Icon={PersonCarryBox} color={colors.primary_blue} respSize={statusRespSize} />,
  2: <SVGIcon Icon={TruckFast} color={colors.primary_blue} respSize={statusRespSize} />,
  3: <SVGIcon Icon={CircleCheck} color={colors.green_blue_bg} respSize={statusRespSize} />,
  4: <SVGIcon Icon={TriangleExclamation} color={colors.warning_red} respSize={statusRespSize} />,
}

const ShipmentDetails = () => {
  const { t } = useTranslation()
  const packageDetails = useSelector(selectPackageDetails)
  const [showMoreModal, setShowMoreModal] = useState(false)
  const [showPopUp, setShowPopUp] = useState(false)
  const packageDetailsLimit = 2
  let leadReferences = []

  const packageDetailItems =
    packageDetails.length > 0
      ? packageDetails.map((packageItem, index) => {
          if (packageItem.isLeadPin) leadReferences = packageItem.references
          return (
            <StyledWrapper key={packageItem.packagepin}>
              <PackageDetailsCard
                shipmentIndex={t('packageDetails.shipment_index', {
                  index: index + 1,
                  totalPackages: packageDetails.length,
                })}
                pin={packageItem.packagepin}
                isLeadPin={packageItem.isLeadPin}
                toolTip={
                  <ToolTip id="tooltip-shipment-details-lead-pin" placement="top">
                    {t('packageDetails.tooltip_content')}
                  </ToolTip>
                }
                status={
                  packageItem.isMissedDelivery
                    ? t('packageDetails.missed_delivery')
                    : packageItem.status
                }
                statusIcon={statusIconMapping[packageItem.statusCode]}
                weight={packageItem.packageWeight}
                shipmentDate={packageItem.shipmentDate}
              />
            </StyledWrapper>
          )
        })
      : []

  const referenceIDHandler = () => {
    setShowPopUp(true)
  }

  const referenceIDButton = (
    <StyledLinkButton
      id="referenceIDBtn"
      variant="link"
      onClick={referenceIDHandler}
      disabled={leadReferences.length === 0}
    >
      {t('packageDetails.reference')}
    </StyledLinkButton>
  )

  const showMoreHandler = () => {
    setShowMoreModal(true)
  }

  const onActionCloseHandler = () => {
    setShowPopUp((prev) => !prev)
  }

  const firstPackage =
    packageDetails.length > 0 ? packageDetails[0] : { service: t('not_available') }
  const firstCharUpperConverter = (input) =>
    input.charAt(0).toUpperCase() + input.slice(1).toLowerCase()

  const fromToTransit =
    packageDetails.length > 0 ? (
      <StyledDisplayContent>
        <span className="addressStyle">
          {`${firstCharUpperConverter(firstPackage.fromAddress.city)}, ${
            firstPackage.fromAddress.province
          }`}
        </span>
        <div className="addressIconStyle">
          <span className="addressLine">&nbsp;</span>
          <div>
            <SVGIcon
              Icon={TruckFast}
              color={colors.primary_blue}
              respSize={{
                phone: { size: '16px' },
                tablet: { size: '24px' },
                desktop: { size: '16px' },
              }}
              style={{ verticalAlign: 'bottom' }}
            />
          </div>
          <span className="addressLine">&nbsp;</span>
        </div>
        <span className="addressStyle">
          {`${firstCharUpperConverter(firstPackage.toAddress.city)}, ${
            firstPackage.toAddress.province
          }`}
        </span>
      </StyledDisplayContent>
    ) : (
      t('cansDoorKnocker.service_unavailable')
    )

  return (
    <CollectionCard
      titleIconPath={PackageDetailsSVG}
      title={t('packageDetails.shipment_details_title')}
      subtitle={t('packageDetails.shipment_details', {
        numPackages: packageDetails.length,
        service: firstPackage.service,
      })}
      id="shipment-details-collection-card"
      displayContent={fromToTransit}
      actionButton={referenceIDButton}
      collectionArray={packageDetailItems.slice(0, packageDetailsLimit)}
    >
      <div style={{ textAlign: 'center' }}>
        <PopUp
          show={showPopUp}
          primaryButton={
            <Button variant="primary" onClick={onActionCloseHandler}>
              {t('buttonActions.close')}
            </Button>
          }
          icon={PackageDetailsSVG}
          title={t('packageDetails.reference')}
          size="md"
        >
          <div style={{ display: 'inline-block', textAlign: 'left' }}>
            <url>
              {leadReferences.map((ref, i) => (
                <li key={`${ref}-${i}`}>{ref}</li>
              ))}
            </url>
          </div>
        </PopUp>
        <ShowMoreModal
          headerTitle={t('packageDetails.shipment_details_title')}
          iconPath={PackageDetailsSVG}
          show={showMoreModal}
          size="md"
          onHide={setShowMoreModal}
        >
          {packageDetailItems}
        </ShowMoreModal>
        {packageDetailItems.length > packageDetailsLimit && (
          <StyledLinkButton id="showMoreBtn" variant="link" onClick={showMoreHandler}>
            {t('packageDetails.see_more')}
          </StyledLinkButton>
        )}
      </div>
    </CollectionCard>
  )
}

export default ShipmentDetails
